import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "GrupoInstructores";
const MODULO_ACTAS = "Actas";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListarGrupoInstructores = ({ refresh }) => {
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  const authConfig = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire("Sesión expirada", "Inicia sesión nuevamente.", "warning").then(
          () => {
            localStorage.removeItem("token");
            navigate("/login");
          }
        );
        return true;
      }

      return false;
    },
    [navigate]
  );

  const getModuloPorNombre = (nombreModulo) => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === normalizarTexto(nombreModulo);
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloGrupoInstructores = useMemo(
    () => getModuloPorNombre(MODULO),
    []
  );

  const moduloActas = useMemo(() => getModuloPorNombre(MODULO_ACTAS), []);

  const puedeCrear =
    moduloGrupoInstructores?.puedeCrear === true ||
    moduloGrupoInstructores?.crear === true ||
    moduloGrupoInstructores?.create === true;

  const puedeActualizar =
    moduloGrupoInstructores?.puedeActualizar === true ||
    moduloGrupoInstructores?.actualizar === true ||
    moduloGrupoInstructores?.editar === true ||
    moduloGrupoInstructores?.update === true;

  const puedeEliminar =
    moduloGrupoInstructores?.puedeEliminar === true ||
    moduloGrupoInstructores?.eliminar === true ||
    moduloGrupoInstructores?.delete === true;

  const puedeLeerActas =
    moduloActas?.puedeLeer === true ||
    moduloActas?.leer === true ||
    moduloActas?.read === true ||
    moduloActas?.ver === true ||
    moduloActas?.puedeVer === true;

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        "/api/grupoinstructores/mis-grupos",
        authConfig()
      );

      const lista = Array.isArray(data?.asignaciones)
        ? data.asignaciones
        : [];

      setAsignaciones([...lista].reverse());
    } catch (error) {
      console.error("Error al cargar asignaciones:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "No se pudo cargar la lista de asignaciones.",
        "error"
      );

      setAsignaciones([]);
    } finally {
      setLoading(false);
    }
  }, [manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  const editAsignacion = (item) => {
    localStorage.setItem(
      "grupoInstructorEdit",
      JSON.stringify({
        _id: item._id,
        grupoObjectId: item.grupoObjectId?._id || "",
        grupoLabel: `${item.grupoObjectId?.fichaGrupo || "Sin ficha"}`,
        instructorObjectId: item.instructorObjectId?._id || "",
        instructorLabel: `${
          item.instructorObjectId?.nombre1Instructor || ""
        } ${item.instructorObjectId?.nombre2Instructor || ""} ${
          item.instructorObjectId?.apellido1Instructor || ""
        } ${item.instructorObjectId?.apellido2Instructor || ""} - ${
          item.instructorObjectId?.documentoInstructor || "Sin documento"
        }`,
        tipoAsignacion: item.tipoAsignacion || "PRINCIPAL",
        estadoAsignacion: item.estadoAsignacion || "ACTIVO",
        observacionAsignacion: item.observacionAsignacion || "",
      })
    );

    navigate(`/grupoinstructores/editar/${item._id}`);
  };

  const deleteAsignacion = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar asignaciones.",
          "warning"
        );
        return;
      }

      const result = await Swal.fire({
        title: "¿Está seguro?",
        text: "El instructor será retirado del grupo.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) return;

      try {
        setDeletingId(id);

        const { data } = await axios.delete(
          `/api/grupoinstructores/${id}`,
          authConfig()
        );

        setAsignaciones((prev) =>
          prev.filter((asignacion) => asignacion._id !== id)
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Asignación eliminada",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar asignación:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
            "No se pudo eliminar la asignación.",
          "error"
        );
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  const dataTabla = useMemo(
    () =>
      asignaciones.map((item) => {
        const instructor = item.instructorObjectId || {};
        const grupo = item.grupoObjectId || {};

        const nombreCompleto = [
          instructor.nombre1Instructor,
          instructor.nombre2Instructor,
          instructor.apellido1Instructor,
          instructor.apellido2Instructor,
        ]
          .filter(Boolean)
          .join(" ");

        return {
          ...item,

          instructorNombre: (
            <div title={nombreCompleto}>
              {nombreCompleto || "Instructor no disponible"}
            </div>
          ),

          documentoInstructor:
            instructor.documentoInstructor || "Sin documento",

          emailInstructor: instructor.emailInstructor || "Sin correo",

          telefonoInstructor: instructor.telefono1Instructor || "Sin teléfono",

          fichaGrupo: grupo.fichaGrupo || "Sin ficha",

          estadoGrupo: grupo.estadoGrupo || "Sin estado",

          cuposGrupo: grupo.cantCuposGrupo || "Sin cupos",

          tipoAsignacionVista: (
            <span className="badge bg-info-subtle text-info">
              {item.tipoAsignacion || "SIN TIPO"}
            </span>
          ),

          estadoAsignacionVista: (
            <span
              className={`badge ${
                item.estadoAsignacion === "ACTIVO"
                  ? "bg-success"
                  : "bg-secondary"
              }`}
            >
              {item.estadoAsignacion || "SIN ESTADO"}
            </span>
          ),

          observacionAsignacion:
            item.observacionAsignacion || "Sin observación",

          funciones: (
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              {puedeLeerActas && (
                <button
                  className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() =>
                    navigate(`/institucional/actas/${grupo.fichaGrupo}`)
                  }
                  title="Listar Actas del Grupo"
                  type="button"
                  disabled={loading || deletingId === item._id || !grupo.fichaGrupo}
                >
                  <i className="fa-solid fa-list-check"></i>
                </button>
              )}

              {puedeActualizar && (
                <button
                  className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  disabled={loading || deletingId === item._id}
                  onClick={() => editAsignacion(item)}
                  title="Editar Asignación"
                  type="button"
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
              )}

              {puedeEliminar && (
                <button
                  className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  disabled={loading || deletingId === item._id}
                  onClick={() => deleteAsignacion(item._id)}
                  title="Eliminar Asignación"
                  type="button"
                >
                  {deletingId === item._id ? (
                    <i className="fa-solid fa-spinner fa-spin text-white"></i>
                  ) : (
                    <i className="fa-solid fa-trash"></i>
                  )}
                </button>
              )}
            </div>
          ),
        };
      }),
    [
      asignaciones,
      puedeLeerActas,
      puedeActualizar,
      puedeEliminar,
      loading,
      deletingId,
      navigate,
      deleteAsignacion,
    ]
  );

  const columns = useMemo(
    () => [
      { Header: "Documento", accessor: "documentoInstructor" },
      { Header: "Instructor", accessor: "instructorNombre" },
      { Header: "Teléfono", accessor: "telefonoInstructor" },
      { Header: "Ficha", accessor: "fichaGrupo" },
      { Header: "Estado Grupo", accessor: "estadoGrupo" },
      { Header: "Cupos", accessor: "cuposGrupo" },
      { Header: "Tipo", accessor: "tipoAsignacionVista" },
      { Header: "Estado", accessor: "estadoAsignacionVista" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Asignar Instructores por Grupo
                </h5>

                <small className="text-secondary">
                  Gestión institucional de instructores asignados a grupos de
                  formación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate("/inicio")}
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      localStorage.removeItem("grupoInstructorEdit");
                      navigate("/grupoinstructores/registrar");
                    }}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status"></div>
                <div className="mt-3">Cargando asignaciones…</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataTabla} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">👨‍🏫</div>

                <div className="fw-semibold">
                  No hay instructores asignados
                </div>

                <div className="small mb-3">
                  Crea la primera asignación para relacionar instructores con
                  grupos.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => {
                      localStorage.removeItem("grupoInstructorEdit");
                      navigate("/grupoinstructores/registrar");
                    }}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear asignación
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarGrupoInstructores;